import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  type HistoryEntry,
  type HistoryKind,
  type LeadRecord,
  type LeadStatus,
  type Assets,
} from '@flashpoint/site-dna';
import { db } from '../firebase';
import { scrubForFirestoreValue } from './scrubFirestore';
import { deleteLeadAssets } from './storage';

const LEADS = 'leads';
const HISTORY = 'history';

function leadDoc(id: string) {
  return doc(db, LEADS, id);
}
function leadsCol() {
  return collection(db, LEADS);
}
function historyCol(leadId: string) {
  return collection(db, LEADS, leadId, HISTORY);
}

function tsToIso(value: unknown): string | undefined {
  if (!value) return undefined;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  return undefined;
}

/** Firestore rejects `undefined` anywhere in document data; strip it recursively. */
function stripUndefinedDeep(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === undefined) continue;
    const nested = stripUndefinedDeep(v);
    if (nested === undefined) continue;
    out[k] = nested;
  }
  return out;
}

function fromFirestoreLead(id: string, data: DocumentData): LeadRecord {
  return {
    ...(data as LeadRecord),
    id,
    createdAt: tsToIso(data.createdAt) ?? new Date().toISOString(),
    updatedAt: tsToIso(data.updatedAt) ?? new Date().toISOString(),
  };
}

function sortLeadsByUpdatedAt(rows: LeadRecord[]): LeadRecord[] {
  return [...rows].sort((a, b) =>
    a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0,
  );
}

/** One-shot read — often returns from local persistence immediately after first visit. */
export async function fetchLeadsOnce(): Promise<LeadRecord[]> {
  const snap = await getDocs(query(leadsCol()));
  const rows = snap.docs.map((d) => fromFirestoreLead(d.id, d.data()));
  return sortLeadsByUpdatedAt(rows);
}

/**
 * Subscribe to all lead docs. Sorted client-side so we do not depend on a
 * Firestore index for `updatedAt` (missing index would only surface as a
 * listener error — previously unhandled, leaving the UI stuck on “Loading…”).
 */
export function onLeadsSnapshot(
  cb: (leads: LeadRecord[]) => void,
  onError?: (message: string) => void,
): Unsubscribe {
  const q = query(leadsCol());
  return onSnapshot(
    q,
    (snap) => {
      try {
        const rows = sortLeadsByUpdatedAt(
          snap.docs.map((d) => fromFirestoreLead(d.id, d.data())),
        );
        cb(rows);
      } catch (err) {
        console.error('[firestore] onLeadsSnapshot parse error', err);
        cb([]);
        onError?.(err instanceof Error ? err.message : 'Failed to load leads');
      }
    },
    (err) => {
      console.error('[firestore] onLeadsSnapshot', err);
      cb([]);
      onError?.(err.message);
    },
  );
}

export function onLeadSnapshot(
  id: string,
  cb: (lead: LeadRecord | null) => void,
): Unsubscribe {
  return onSnapshot(leadDoc(id), (snap) => {
    if (!snap.exists()) return cb(null);
    cb(fromFirestoreLead(snap.id, snap.data()));
  });
}

export async function getLead(id: string): Promise<LeadRecord | null> {
  const snap = await getDoc(leadDoc(id));
  if (!snap.exists()) return null;
  return fromFirestoreLead(snap.id, snap.data());
}

/**
 * Read current lead assets from Firestore, merge, then write `siteDna.assets`
 * as one map. Avoids brittle dotted-field updates (`siteDna.assets.logo`) that
 * may not surface in snapshots the same way across SDK merges.
 */
export async function mergeSiteDnaAssets(
  leadId: string,
  mutator: (prev: Assets) => Assets,
): Promise<void> {
  const ref = leadDoc(leadId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Lead not found');
  const data = snap.data() as DocumentData & { siteDna?: { assets?: Assets } };
  const prev = data.siteDna?.assets ?? {};
  const next = mutator({ ...prev });
  await updateDoc(ref, {
    'siteDna.assets': scrubForFirestoreValue(next),
    updatedAt: serverTimestamp(),
  });
}

export async function createLead(record: LeadRecord): Promise<void> {
  const data = stripUndefinedDeep(record) as Record<string, unknown>;
  await setDoc(leadDoc(record.id), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function patchLead(
  id: string,
  patch: Partial<Omit<LeadRecord, 'id' | 'ownerUid' | 'createdAt'>>,
): Promise<void> {
  const data = stripUndefinedDeep(patch) as Record<string, unknown>;
  await updateDoc(leadDoc(id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteLead(id: string): Promise<void> {
  await deleteLeadAssets(id);

  const history = historyCol(id);
  while (true) {
    const snap = await getDocs(query(history, fsLimit(400)));
    if (snap.empty) break;
    const batch = writeBatch(db);
    for (const d of snap.docs) batch.delete(d.ref);
    await batch.commit();
  }

  await deleteDoc(leadDoc(id));
}

export async function updateLeadStatus(id: string, status: LeadStatus): Promise<void> {
  await patchLead(id, { leadStatus: status });
}

/**
 * Append an entry to a lead's history sub-collection. Caller supplies kind +
 * payload + actor; ts is server-stamped.
 */
export async function appendHistoryEntry(
  leadId: string,
  entry: Omit<HistoryEntry, 'id' | 'ts'> & { ts?: string },
): Promise<void> {
  await addDoc(historyCol(leadId), {
    ...entry,
    ts: entry.ts ?? serverTimestamp(),
  });
}

export function onHistorySnapshot(
  leadId: string,
  cb: (entries: HistoryEntry[]) => void,
  opts: { limit?: number } = {},
): Unsubscribe {
  const constraints = [orderBy('ts', 'desc')];
  if (opts.limit) constraints.push(fsLimit(opts.limit) as never);
  const q = query(historyCol(leadId), ...constraints);
  return onSnapshot(q, (snap) => {
    cb(
      snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ts: tsToIso(data.ts) ?? new Date().toISOString(),
          actorUid: data.actorUid,
          actorEmail: data.actorEmail,
          kind: data.kind as HistoryKind,
          payload: data.payload ?? {},
        };
      }),
    );
  });
}
